import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Send, Loader2, Search } from "lucide-react";
import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import ChatHeaderPill from "@/components/ChatHeaderPill";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useMessages } from "@/contexts/MessagesContext";

function timeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function MessagesPage() {
  const { user } = useAuth();
  const { refreshUnread } = useMessages();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);  // peer object
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [filter, setFilter] = useState("");
  const [peerInfo, setPeerInfo] = useState({ online: false, typing: false, lastSeen: null });
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastTypingRef = useRef(0);

  const loadThreads = async () => {
    setLoadingThreads(true);
    try {
      const { data } = await api.get("/dm/threads");
      const list = Array.isArray(data) ? data : [];
      setThreads(list);
      if (!active && list.length > 0) {
        setActive(list[0].peer);
      }
    } catch { /* ignore */ } finally { setLoadingThreads(false); }
  };

  const loadMessages = async () => {
    if (!active) return;
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/dm/${active.user_id}`, { params: { limit: 100 } });
      setMessages(Array.isArray(data?.messages) ? data.messages : []);
      if (data?.peer) {
        setPeerInfo({ online: !!data.peer.online, typing: !!data.peer.typing, lastSeen: data.peer.last_seen || null });
      }
      try { await api.post(`/dm/${active.user_id}/read`); refreshUnread(); }
      catch { /* ignore */ }
    } catch { /* ignore */ } finally { setLoadingMsgs(false); }
  };

  const sendTypingPing = () => {
    if (!active) return;
    const now = Date.now();
    if (now - lastTypingRef.current < 3000) return;
    lastTypingRef.current = now;
    api.post(`/dm/${active.user_id}/typing`).catch(() => {});
  };

  useEffect(() => { loadThreads(); /* eslint-disable-next-line */ }, [user?.user_id]);
  useEffect(() => {
    loadMessages();
    clearInterval(pollRef.current);
    if (active) pollRef.current = setInterval(loadMessages, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.user_id]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/dm/${active.user_id}`, { text: text.trim() });
      setMessages((prev) => [...prev, data]);
      setText("");
      // Optimistically bump the thread row
      loadThreads();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[var(--tt-bg)] flex items-center justify-center text-white">
        <div className="text-center">
          <h2 className="font-display text-2xl font-bold mb-2">Sign in to view your inbox</h2>
          <Link to="/login" className="text-[var(--tt-primary)] font-semibold hover:underline">Go to login →</Link>
        </div>
      </div>
    );
  }

  const filtered = threads.filter((t) =>
    !filter.trim() || (t.peer.name || "").toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="min-h-screen bg-[var(--tt-bg)] text-[var(--tt-text)]">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 min-w-0">
          <Header />
          <main className="px-3 md:px-8 py-6">
            <div className="flex items-center gap-3 mb-4">
              <MessageSquare className="w-6 h-6 text-[var(--tt-purple)]" />
              <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">Inbox</h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-3 md:gap-4 bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden h-[70vh]">
              {/* Threads rail */}
              <aside className="border-r border-[var(--tt-border)] flex flex-col min-h-0">
                <div className="px-3 py-2 border-b border-[var(--tt-border)] flex items-center gap-2 bg-[var(--tt-surface-2)]">
                  <Search className="w-3.5 h-3.5 text-[var(--tt-text-muted)]" />
                  <input
                    type="text"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search conversations"
                    data-testid="messages-search-input"
                    className="flex-1 bg-transparent outline-none text-xs placeholder:text-[var(--tt-text-faint)]"
                  />
                </div>
                <div className="flex-1 overflow-y-auto" data-testid="messages-threads">
                  {loadingThreads ? (
                    <div className="text-xs text-[var(--tt-text-muted)] text-center py-8">Loading...</div>
                  ) : filtered.length === 0 ? (
                    <div className="text-xs text-[var(--tt-text-muted)] text-center py-12 px-4">
                      No conversations yet. Visit a user profile and click <span className="text-[var(--tt-primary)] font-semibold">Message</span>.
                    </div>
                  ) : (
                    <ul>
                      {filtered.map((t) => {
                        const isActive = active?.user_id === t.peer.user_id;
                        return (
                          <li
                            key={t.thread_id}
                            onClick={() => setActive(t.peer)}
                            data-testid={`messages-thread-${t.peer.user_id}`}
                            className={`px-3 py-3 cursor-pointer border-b border-[var(--tt-border)] last:border-b-0
                              ${isActive ? "bg-[var(--tt-primary)]/[0.08]" : t.unread > 0 ? "bg-[var(--tt-primary)]/[0.04]" : "hover:bg-white/[0.04]"}`}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="relative shrink-0">
                                {t.peer.picture ? (
                                  <img src={t.peer.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                                ) : (
                                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-xs font-bold">
                                    {(t.peer.name || "?")[0].toUpperCase()}
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-semibold text-sm truncate">{t.peer.name}</span>
                                  <span
                                    className={`text-[9px] uppercase tracking-wider font-bold ml-1 ${t.peer.online ? "text-[#1CE783]" : "text-red-400/80"}`}
                                  >
                                    {t.peer.online ? "Online" : "Offline"}
                                  </span>
                                  <span className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt ml-auto">{timeShort(t.last_message.created_at)}</span>
                                </div>
                                <div className="text-xs text-[var(--tt-text-muted)] truncate mt-0.5">
                                  {t.last_message.from_me ? "You: " : ""}{t.last_message.text}
                                </div>
                              </div>
                              {t.unread > 0 && (
                                <span className="ml-1 mt-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--tt-primary)] text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                                  {t.unread}
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </aside>

              {/* Chat pane */}
              <section className="flex flex-col min-h-0">
                {!active ? (
                  <div className="flex-1 flex items-center justify-center text-sm text-[var(--tt-text-muted)]">
                    Pick a conversation to start chatting.
                  </div>
                ) : (
                  <>
                    <header className="border-b border-[var(--tt-border)]">
                      <ChatHeaderPill
                        peer={active}
                        online={peerInfo.online}
                        typing={peerInfo.typing}
                        lastSeen={peerInfo.lastSeen}
                      />
                    </header>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2" data-testid="messages-pane">
                      {loadingMsgs && messages.length === 0 ? (
                        <div className="flex items-center justify-center gap-2 text-xs text-[var(--tt-text-muted)] py-6">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center text-xs text-[var(--tt-text-muted)] py-12">
                          No messages yet. Say hi to <span className="text-[var(--tt-primary)] font-semibold">@{active.name}</span>.
                        </div>
                      ) : (
                        messages.map((m) => {
                          const mine = m.from_user_id === user.user_id;
                          return (
                            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                              <div className={`max-w-[70%] px-3 py-2 rounded-2xl text-sm break-words
                                ${mine ? "bg-[var(--tt-primary)] text-white rounded-br-sm" : "bg-[var(--tt-surface-2)] text-zinc-100 rounded-bl-sm"}`}
                              >
                                <div className="whitespace-pre-wrap">{m.text}</div>
                                <div className={`text-[9px] uppercase tracking-wider mt-1 ${mine ? "text-white/70" : "text-[var(--tt-text-faint)]"}`}>
                                  {timeShort(m.created_at)}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    <form onSubmit={send} className="border-t border-[var(--tt-border)] px-3 py-2.5 flex items-end gap-2">
                      <textarea
                        value={text}
                        onChange={(e) => { setText(e.target.value); if (e.target.value.trim()) sendTypingPing(); }}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder={`Message @${active.name}...`}
                        rows={1}
                        maxLength={4000}
                        data-testid="messages-input"
                        className="flex-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-2xl px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] resize-none max-h-32"
                      />
                      <button
                        type="submit"
                        disabled={sending || !text.trim()}
                        data-testid="messages-send"
                        className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white w-10 h-10 flex items-center justify-center rounded-full disabled:opacity-50"
                      >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </button>
                    </form>
                  </>
                )}
              </section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
